import { useCallback, useEffect, useRef } from 'react'
import { useProductEditor } from '../product-editor-context'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, X, Layers, RotateCcw } from 'lucide-react'
import type { ProductOption, ProductVariant } from '@/types/database'
import { useState, KeyboardEvent } from 'react'
import { slugify } from '@/lib/utils'

function generateId() {
  return crypto.randomUUID().slice(0, 8)
}

function generateVariants(options: ProductOption[], existingVariants: ProductVariant[], baseSku?: string): ProductVariant[] {
  if (options.length === 0) return []

  // Build cartesian product of option values
  const combos: Record<string, string>[][] = options.reduce<Record<string, string>[][]>(
    (acc, option) => {
      if (option.values.length === 0) return acc
      if (acc.length === 0) {
        return option.values.map(v => [{ [option.name]: v }])
      }
      const result: Record<string, string>[][] = []
      for (const existing of acc) {
        for (const value of option.values) {
          result.push([...existing, { [option.name]: value }])
        }
      }
      return result
    },
    []
  )

  // Convert combos to flat option_values objects
  const variantCombos = combos.map(combo => {
    const optionValues: Record<string, string> = {}
    combo.forEach(pair => {
      Object.assign(optionValues, pair)
    })
    return optionValues
  })

  // Match existing variants by option_values to preserve customizations
  const existingMap = new Map<string, ProductVariant>()
  existingVariants.forEach(v => {
    const key = JSON.stringify(v.option_values, Object.keys(v.option_values).sort())
    existingMap.set(key, v)
  })

  return variantCombos.map((optionValues, i) => {
    const key = JSON.stringify(optionValues, Object.keys(optionValues).sort())
    const existing = existingMap.get(key)
    if (existing) {
      return { ...existing, position: i }
    }
    const suffix = Object.values(optionValues)
      .map(v => slugify(v))
      .filter(Boolean)
      .join('-')
    const autoSku = baseSku && suffix ? `${baseSku}-${suffix}` : (baseSku || null)
    return {
      id: generateId(),
      option_values: optionValues,
      sku: autoSku,
      sku_locked: false,
      price_adjustment: 0,
      image_url: null,
      available: true,
      position: i,
    }
  })
}

function OptionValueInput({ values, onChange }: { values: string[]; onChange: (values: string[]) => void }) {
  const [inputValue, setInputValue] = useState('')

  const addValue = useCallback((value: string) => {
    const trimmed = value.trim()
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed])
    }
    setInputValue('')
  }, [values, onChange])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addValue(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  const removeValue = (index: number) => {
    onChange(values.filter((_, i) => i !== index))
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-red-500 focus-within:ring-offset-1">
      {values.map((value, i) => (
        <Badge key={i} variant="secondary" className="gap-1 pl-2 pr-1">
          {value}
          <button
            type="button"
            onClick={() => removeValue(i)}
            className="ml-0.5 rounded-full p-0.5 hover:bg-zinc-300"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => inputValue && addValue(inputValue)}
        placeholder={values.length === 0 ? 'Type and press Enter' : ''}
        className="min-w-[80px] flex-1 border-0 bg-transparent py-1 text-sm outline-none placeholder:text-zinc-400"
      />
    </div>
  )
}

export function OptionsVariantsSection() {
  const { form, options, setOptions, variants, setVariants } = useProductEditor()
  const productSku = form.watch('sku') || ''

  const addOption = () => {
    if (options.length >= 3) return
    const newOption: ProductOption = {
      id: generateId(),
      name: '',
      values: [],
      position: options.length,
    }
    setOptions([...options, newOption])
  }

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index).map((o, i) => ({ ...o, position: i }))
    setOptions(newOptions)
    setVariants(generateVariants(newOptions, variants, productSku))
  }

  const updateOptionName = (index: number, name: string) => {
    const newOptions = [...options]
    newOptions[index] = { ...newOptions[index], name }
    setOptions(newOptions)
    setVariants(generateVariants(newOptions, variants, productSku))
  }

  const updateOptionValues = (index: number, values: string[]) => {
    const newOptions = [...options]
    newOptions[index] = { ...newOptions[index], values }
    setOptions(newOptions)
    setVariants(generateVariants(newOptions, variants, productSku))
  }

  const updateVariant = (variantIndex: number, updates: Partial<ProductVariant>) => {
    const newVariants = [...variants]
    newVariants[variantIndex] = { ...newVariants[variantIndex], ...updates }
    setVariants(newVariants)
  }

  const getVariantLabel = (variant: ProductVariant) => {
    return Object.entries(variant.option_values).map(([, v]) => v).join(' / ')
  }

  // Propagate product SKU changes to unlocked variant SKUs
  const prevSkuRef = useRef(productSku)
  useEffect(() => {
    if (prevSkuRef.current === productSku) return
    prevSkuRef.current = productSku
    if (!productSku || variants.length === 0) return

    const updated = variants.map(v => {
      if (v.sku_locked) return v
      const suffix = Object.values(v.option_values).map(val => slugify(val)).filter(Boolean).join('-')
      return { ...v, sku: suffix ? `${productSku}-${suffix}` : productSku }
    })

    if (updated.some((v, i) => v.sku !== variants[i].sku)) {
      setVariants(updated)
    }
  }, [productSku, variants, setVariants])

  const autoFillVariantSkus = () => {
    if (!productSku) return
    const updated = variants.map(v => {
      const suffix = Object.values(v.option_values).map(val => slugify(val)).filter(Boolean).join('-')
      return { ...v, sku: suffix ? `${productSku}-${suffix}` : productSku, sku_locked: false }
    })
    setVariants(updated)
  }

  const hasOptions = options.length > 0
  const hasValidOptions = options.some(o => o.name && o.values.length > 0)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="size-5 text-zinc-500" />
            <div>
              <CardTitle className="text-base">Options & Variants</CardTitle>
              <CardDescription>
                Add product options like Size or Color to create variants
              </CardDescription>
            </div>
          </div>
          {options.length < 3 && (
            <Button type="button" variant="outline" size="sm" onClick={addOption}>
              <Plus className="mr-1 size-3" /> Add option
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Options */}
        {options.map((option, i) => (
          <div key={option.id} className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-zinc-500">Option name</label>
                <Input
                  value={option.name}
                  onChange={e => updateOptionName(i, e.target.value)}
                  placeholder="e.g., Size, Color, Style"
                  className="mt-1"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeOption(i)}
                className="mt-5 text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500">Option values</label>
              <div className="mt-1">
                <OptionValueInput
                  values={option.values}
                  onChange={values => updateOptionValues(i, values)}
                />
              </div>
              <p className="mt-1 text-xs text-zinc-400">Press Enter or comma to add a value</p>
            </div>
          </div>
        ))}

        {!hasOptions && (
          <p className="text-sm text-zinc-400 text-center py-4">
            No options added. Products without options are sold as-is.
          </p>
        )}

        {/* Variants Table */}
        {hasValidOptions && variants.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-medium text-zinc-700">
                Variants ({variants.length})
              </h4>
              {productSku && (
                <Button type="button" variant="outline" size="sm" onClick={autoFillVariantSkus}>
                  <RotateCcw className="mr-1 size-3" /> Auto-fill SKUs
                </Button>
              )}
            </div>
            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-zinc-50 text-left">
                    <th className="px-3 py-2 font-medium text-zinc-600">Variant</th>
                    <th className="px-3 py-2 font-medium text-zinc-600">SKU</th>
                    <th className="px-3 py-2 font-medium text-zinc-600">Price +/-</th>
                    <th className="px-3 py-2 font-medium text-zinc-600 text-center">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {variants.map((variant, i) => (
                    <tr key={variant.id} className="bg-white">
                      <td className="px-3 py-2 font-medium text-zinc-900">
                        {getVariantLabel(variant)}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={variant.sku || ''}
                          onChange={e => updateVariant(i, { sku: e.target.value || null, sku_locked: true })}
                          placeholder="Auto from product SKU"
                          className="h-8 w-40"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={variant.price_adjustment}
                            onChange={e => updateVariant(i, { price_adjustment: parseFloat(e.target.value) || 0 })}
                            className="h-8 w-24"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch
                          checked={variant.available}
                          onCheckedChange={(checked: boolean) => updateVariant(i, { available: checked })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
